import React, { useState } from "react";

// Shared "Change Password" modal component.
// Usage: <ChangePassword token={token} onClose={() => setShowChangePw(false)} />
export default function ChangePassword({ token, onClose }) {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async () => {
    setError("");
    setSuccess("");

    if (!form.current_password || !form.new_password || !form.confirm_password) {
      setError("All fields are required");
      return;
    }
    if (form.new_password.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError("New password and confirm password do not match");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/users/change-password", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: form.current_password,
          new_password: form.new_password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to change password");

      setSuccess("Password changed successfully!");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Change Password</div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        <div style={styles.formGroup}>
          <label style={styles.label}>Current Password *</label>
          <input
            type="password"
            style={styles.input}
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            placeholder="Enter current password"
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>New Password *</label>
          <input
            type="password"
            style={styles.input}
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            placeholder="At least 6 characters"
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Confirm New Password *</label>
          <input
            type="password"
            style={styles.input}
            value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            placeholder="Re-enter new password"
          />
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.btnOutline} onClick={onClose}>Cancel</button>
          <button style={styles.btnPrimary} onClick={submit} disabled={submitting}>
            {submitting ? "Saving..." : "Change Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16 },
  modal: { background: "#fff", borderRadius: 10, width: "100%", maxWidth: 400, padding: 24 },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#111827" },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#6b7280", padding: 0, lineHeight: 1 },
  formGroup: { display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151" },
  input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" },
  btnPrimary: { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 5, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  btnOutline: { background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 5, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 14 },
  successBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 14 },
};
